import {ChangeDetectionStrategy, Component, computed, effect, inject, input} from '@angular/core';
import {AbstractControl, FormArray, FormGroup, FormGroupDirective} from '@angular/forms';
import {toSignal} from '@angular/core/rxjs-interop';
import _ from 'lodash';
import {HarbourDuesContainerHandlingCountForm} from '../../../../../types';
import {HarbourDuesBerthVisit} from '@portbase/bezoekschip-service-typescriptmodels';
import {
  ContainerHandlingColumnTotals,
  HarbourDuesContainerHandlingService
} from '../../../../../services/harbour-dues-container-handling.service';
import {containerCountsTableValidator} from '../../../../../services/harbour-dues-form.service';

@Component({
  selector: 'app-container-handling-rotterdam',
  templateUrl: './container-handling-rotterdam.component.html',
  styleUrl: './container-handling-rotterdam.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ContainerHandlingRotterdamComponent {
  readonly berthVisitNumber = input.required<number>();
  readonly mode = input.required<ContainerHandlingMode>();
  readonly showValidationErrors = input<boolean>(false);

  private readonly form = inject(FormGroupDirective);
  private readonly containerHandlingService = inject(HarbourDuesContainerHandlingService);

  private readonly containerHandlingKey = computed(() =>
    this.mode() === ContainerHandlingMode.DISCHARGE ? 'dischargedContainerHandling' : 'loadedContainerHandling');

  protected readonly containerHandlingGroups = computed(() => {
    const berthVisits = this.form.control.get('berthVisits') as FormArray;
    const berthVisitGroup = berthVisits.at(this.berthVisitNumber()) as FormGroup;
    return berthVisitGroup.get(this.containerHandlingKey()) as FormArray<FormGroup<HarbourDuesContainerHandlingCountForm>>;
  });

  private readonly formValues = toSignal<{berthVisits: HarbourDuesBerthVisit[]}>(
    this.form.control.valueChanges,
    {initialValue: this.form.control.value}
  );

  private readonly rows = computed(() => {
    const berthVisit = this.formValues()?.berthVisits?.at(this.berthVisitNumber());
    return this.mode() === ContainerHandlingMode.DISCHARGE
      ? berthVisit?.dischargedContainerHandling
      : berthVisit?.loadedContainerHandling;
  }, {equal: (before, after) => _.isEqual(before, after)});

  protected readonly columnTotals = computed<ContainerHandlingColumnTotals>(
    () => this.containerHandlingService.columnTotals(this.rows())
  );

  constructor() {
    effect(onCleanup => {
      const containerHandling = this.containerHandlingGroups();
      containerHandling.addValidators(containerCountsTableValidator);
      containerHandling.updateValueAndValidity({emitEvent: false});

      onCleanup(() => {
        containerHandling.removeValidators(containerCountsTableValidator);
        containerHandling.updateValueAndValidity({emitEvent: false});
      });
    });
  }

  protected readonly hasNoContainerCountsError = computed(() => {
    this.formValues();
    return this.containerHandlingGroups()?.hasError('noContainerCounts') ?? false;
  });

  protected showFieldError(control: AbstractControl): boolean {
    return control.invalid || (this.hasNoContainerCountsError() && this.showValidationErrors());
  }

  protected rowTotal(group: FormGroup<HarbourDuesContainerHandlingCountForm>): number {
    return this.containerHandlingService.rowTotal({
      full: group.controls.full.value,
      empty: group.controls.empty.value
    });
  }

  protected formatContainerType(group: FormGroup<HarbourDuesContainerHandlingCountForm>): string {
    const containerType = group.controls.containerType.value;
    return containerType ? this.containerHandlingService.formatContainerType(containerType) : '';
  }
}

export enum ContainerHandlingMode {
  LOADING = "LOADING",
  DISCHARGE = "DISCHARGE"
}
